import { Card, Menu, Typography, Button, Tooltip, Avatar } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import {
  Calendar,
  Camera,
  Clock,
  FileText,
  LogOut,
  Package,
  User,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NavItem } from "~/components/staff/types";
import { useAuth, useApp } from "~/store";
import { getClientName } from "~/utils";

const { Text } = Typography;

interface StaffSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export const StaffSidebar: FC<StaffSidebarProps> = ({ 
  isCollapsed, 
  onToggleCollapse 
}) => {
  const { user, logout } = useAuth();
  const { setCollapsed } = useApp();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState<string>("assignments");
  
  const navItems: NavItem[] = [
    { id: "assignments", icon: <Calendar size={20} />, label: "Assignments" },
    { id: "inventory", icon: <Package size={20} />, label: "Inventory" },
    { id: "time-tracking", icon: <Clock size={20} />, label: "Time Tracking" },
    { id: "availability", icon: <Calendar size={20} />, label: "Availability" },
    { id: "profile", icon: <User size={20} />, label: "Profile" },
    { id: "photos", icon: <Camera size={20} />, label: "Photos" },
  ];

  // Update activeItem based on current path
  useEffect(() => {
    const currentPath = location.pathname.split("/").pop() || "assignments";
    setActiveItem(currentPath);
  }, [location.pathname]);

  // Handle menu item click
  const handleMenuItemClick = (key: string) => {
    setActiveItem(key);
    if (window.innerWidth < 1024) {
      setCollapsed(false);
    }
  };

  // Get user initials for collapsed view
  const getUserInitials = () => {
    if (!user) return "U";
    const name = getClientName(user);
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return (name[0] || "U").toUpperCase();
  };

  const getUserName = () => {
    return user ? getClientName(user) : "User";
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Toggle Button */}
      <div className="p-3 border-b border-gray-200">
        <Button
          type="text"
          icon={isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggleCollapse}
          className="w-full h-10 flex items-center justify-center hover:bg-blue-50"
          style={{
            fontSize: '16px',
            color: '#1890ff',
          }}
        />
      </div>

      {/* User Info Section */}
      <div className="p-4 border-b border-gray-200">
        {isCollapsed ? (
          <div className="flex justify-center">
            <Tooltip title={getUserName()} placement="right">
              <Avatar 
                size={40}
                style={{ 
                  backgroundColor: '#1890ff',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {getUserInitials()}
              </Avatar>
            </Tooltip>
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <Avatar 
              size={40}
              style={{ 
                backgroundColor: '#1890ff',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {getUserInitials()}
            </Avatar>
            <div className="flex-1 min-w-0">
              <Text strong className="text-sm block truncate">
                {getUserName()}
              </Text>
              <Text type="secondary" className="text-xs block">
                {user?.role}
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* Team & Vehicle Info - Only show when expanded */}
      {!isCollapsed && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="space-y-3">
            <div>
              <Text type="secondary" className="text-xs font-medium">
                ASSIGNED TEAM
              </Text>
              <Text className="block text-sm mt-1">
                -
              </Text>
            </div>
            <div>
              <Text type="secondary" className="text-xs font-medium">
                VEHICLE
              </Text>
              <Text className="block text-sm mt-1">
                -
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <div className="flex-1 overflow-y-auto">
        <Menu
          mode="inline"
          selectedKeys={[activeItem]}
          onClick={({ key }) => handleMenuItemClick(key)}
          className="border-r-0 h-full"
          style={{
            backgroundColor: 'transparent',
            border: 'none',
          }}
          items={[
            ...navItems.map((item) => ({
              key: item.id,
              icon: isCollapsed ? (
                <Tooltip title={item.label} placement="right">
                  {item.icon}
                </Tooltip>
              ) : item.icon,
              label: isCollapsed ? null : (
                <Link to={item.id} className="text-gray-700 hover:text-blue-600">
                  {item.label}
                </Link>
              ),
              style: {
                margin: '4px 8px',
                borderRadius: '6px',
                height: '44px',
                lineHeight: '44px',
              },
            })),
            {
              type: 'divider',
              style: { margin: '8px 16px' },
            },
            {
              key: 'logout',
              icon: isCollapsed ? (
                <Tooltip title="Sign Out" placement="right">
                  <LogOut size={20} />
                </Tooltip>
              ) : <LogOut size={20} />,
              label: isCollapsed ? null : 'Sign Out',
              danger: true,
              onClick: logout,
              style: {
                margin: '4px 8px',
                borderRadius: '6px',
                height: '44px',
                lineHeight: '44px',
              },
            },
          ]}
        />
      </div>
    </div>
  );
};